import { Injectable, inject } from '@angular/core';
import { EMPTY, interval, Subscription, switchMap, catchError, filter } from 'rxjs';
import { AppData } from '../data';
import { ServerProxyService } from './server-proxy.service';
import { UtilityService } from './utility/utility.service';
import { LiveTelemetryPayload } from '../types/live-telemetry.type';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class TelemetryService {
  private readonly data = inject(AppData);
  private readonly proxy = inject(ServerProxyService);
  private readonly utilityService = inject(UtilityService);

  private telemetrySubscription: Subscription | null = null;

  public startTelemetry(): void {
    if (!environment.features.liveTelemetry.enabled) {
      return;
    }

    if (this.telemetrySubscription) {
      return;
    }

    console.info('[TelemetryService] Starting live telemetry');

    this.telemetrySubscription = interval(environment.features.liveTelemetry.intervalMs)
      .pipe(
        filter(() => this.canSendTelemetry()),
        switchMap(() => {
          const payload = this.buildTelemetryPayload();
          if (!payload) {
            return EMPTY;
          }

          return this.proxy.sendLiveTelemetry(payload).pipe(
            catchError(error => {
              console.error('[TelemetryService] Telemetry request failed:', error?.message || error);
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe(response => {
        if (response) {
          console.info('[TelemetryService] Telemetry sent successfully, eventId:', response.eventId);
        }
      });
  }

  public stopTelemetry(): void {
    if (this.telemetrySubscription) {
      console.info('[TelemetryService] Stopping live telemetry');
      this.telemetrySubscription.unsubscribe();
      this.telemetrySubscription = null;
    }
  }

  private canSendTelemetry(): boolean {
    const hasSession = !!this.data.userSession?.sessionId;
    const hasRoute = !!this.data.route?.id;
    const hasGps = !!this.data.gpsReport;
    const isOnline = !!this.data.connectionStatus?.connected;

    return hasSession && hasRoute && hasGps && isOnline;
  }

  private buildTelemetryPayload(): LiveTelemetryPayload | null {
    try {
      const { userSession, route, gpsReport, localSettings } = this.data;

      if (!userSession?.sessionId || !route || !gpsReport || !localSettings) {
        return null;
      }

      const now = new Date();
      const payload: LiveTelemetryPayload = {
        tenantId: localSettings.customerName,
        vehicleId: String(route.vehicleId ?? ''),
        driverId: String(route.driverId ?? ''),
        routeId: String(route.id ?? ''),
        ...(route.runId && { runId: String(route.runId) }),
        sessionId: userSession.sessionId,
        latitude: gpsReport.latitude,
        longitude: gpsReport.longitude,
        timestamp: now.toISOString(),
        messageId: this.utilityService.generateGuid(),
        speed: gpsReport.speed,
        direction: gpsReport.direction,
        accuracy: gpsReport.accuracy,
        accuracyMeters: gpsReport.accuracy,
        odometer: gpsReport.odometer,
        heading: gpsReport.heading,
        source: 'DRIVER_APP',
        ...(gpsReport.occuranceDate && {
          occuranceDate: gpsReport.occuranceDate.toISOString()
        }),
        ...(gpsReport.dateTimeReported && {
          dateTimeReported: gpsReport.dateTimeReported.toISOString()
        }),
      };

      return payload;
    } catch (error) {
      console.error('[TelemetryService] Error building telemetry payload:', error);
      return null;
    }
  }
}
